import { useMemo, useEffect, useState, useCallback } from "react";
import { CheckboxGroup, Form, FormInstance, useToast, Icon, Icons, SelectOption, Spin } from "@ir/ui";
import {
  DisputeDataForSubmit,
  DisputeDocument,
  RaiseDisputeModalProps,
  RaiseDisputeUnfiFormValues,
  RaiseDisputeUnfiPortalFormValues,
} from "./RaiseDisputeInterface";
import { DisputeType, IsDocumentsRequiredConfig } from "@/core/repositories/deduction";
import { GetDisputeConfigDTO } from "@/core/dto";
import { DeductionDisputeRepository } from "@/infrastructure/repositories/deduction/DeductionDispute.repository";
import { GetDisputeConfigUseCase } from "@/core/usecases/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { useUser, useWorkspace } from "@/hooks";
import { createLogger } from "@/infrastructure/services/logger";
import { DistributionCenter } from "@/core/entities/lookup";
import { Deduction } from "@/core/entities/deduction";
import DisputeNotesField from "./DisputeNotesField";
import UnfiPortalFormContent from "./utils/UnfiPortalFormContent";
import UnfiEmailFormContent from "./utils/UnfiEmailFormContent";

const client = GraphQlAdapter.getInstance().getClient();

const PORTAL_FORM_KEYS = new Set(["UNFI_EMAIL", "UNFI_PORTAL_DISPUTE", "SUPERVALU_DISPUTE"]);

const PORTAL_DISPUTE_SUBMIT_KEYS = new Set(["UNFI_PORTAL_DISPUTE", "SUPERVALU_DISPUTE"]);

const isPortalFormKey = (key: string | null): boolean => {
  return PORTAL_FORM_KEYS.has(key?.toUpperCase() ?? "");
};

const isPortalDisputeSubmitKey = (key: string | null): boolean => {
  return PORTAL_DISPUTE_SUBMIT_KEYS.has(key?.toUpperCase() ?? "");
};

const isSuperValuPurchaser = (purchaserKey: string | undefined): boolean => {
  return purchaserKey?.toUpperCase() === "SUPERVALU";
};

interface RiseDisputeUnfiFormProps extends RaiseDisputeModalProps {
  deduction: Deduction;
  disputeForm: FormInstance<RaiseDisputeUnfiFormValues>;
  onDisputeSubmit: (disputeData: DisputeDataForSubmit) => void;
  onUnfiEmailPreview: (disputeData: DisputeDataForSubmit) => void;
  deductionInvoiceAmount: number;
  remittanceItemNetAmount: number | null;
  documents: DisputeDocument[];
}

const RiseDisputeUnfiForm = ({
  documents,
  disputeForm,
  onDisputeSubmit,
  onUnfiEmailPreview,
  remittanceItemNetAmount,
  deductionInvoiceAmount,
  deduction,
  checkNumber,
  claimNumber,
}: RiseDisputeUnfiFormProps) => {
  const logger = useCallback(() => createLogger("RaiseDisputeUnfiForm"), []);
  const [disputeTypes, setDisputeTypes] = useState<DisputeType[]>([]);
  const [disputeContactInfoList, setDisputeContactInfoList] = useState<{ label: string; value: string }[]>([]);
  const [isDisputeTypesLoading, setIsDisputeTypesLoading] = useState(false);
  const [portalKey, setPortalKey] = useState<string | null>(null);
  const [isDocumentsRequiredConfig, setIsDocumentsRequiredConfig] = useState<IsDocumentsRequiredConfig[]>([]);
  const [distributionCenters, setDistributionCenters] = useState<DistributionCenter[]>([]);
  const [vendorNumber, setVendorNumber] = useState<string | null>(null);
  const [noteTemplate, setNoteTemplate] = useState<string | null>(null);
  const [dataProviderType, setDataProviderType] = useState<string | null>(null);
  const { workspace } = useWorkspace();
  const { user } = useUser();
  const { error: errorToast } = useToast();

  const [isUnfiFormLoading, setIsUnfiFormLoading] = useState(true);

  const isPortalOnlyPurchaser = isSuperValuPurchaser(deduction?.purchaserKey);
  const isPortalForm = isPortalOnlyPurchaser || isPortalFormKey(portalKey);

  const getDistributionCenters = (): SelectOption[] => {
    if (!distributionCenters || !Array.isArray(distributionCenters)) {
      return [];
    }
    return distributionCenters.map(center => ({
      label: center.dcName,
      value: center.dcName.toString(),
    }));
  };

  const getUnfiDocuments = (): SelectOption[] => {
    const compareDocuments = (a: DisputeDocument, b: DisputeDocument) => {
      if (a.deletable && !b.deletable) return 1;
      if (!a.deletable && b.deletable) return -1;
      return 0;
    };
    return [...documents].sort(compareDocuments).map(document => ({
      label: document.name,
      value: document.id.toString(),
    }));
  };

  const getDisputeTypes = (): SelectOption[] => {
    if (!disputeTypes || !Array.isArray(disputeTypes)) {
      return [];
    }
    return disputeTypes.map(type => ({
      label: type.disputeTypeName,
      value: type.disputeTypeId.toString(),
    }));
  };

  const isSingleDisputeType = getDisputeTypes().length === 1;

  const isDocumentsRequired = useMemo(() => {
    return (
      isDocumentsRequiredConfig.find(config => config.deductionTypeId === deduction?.deductionTypeId)
        ?.isDocumentsRequired ?? false
    );
  }, [isDocumentsRequiredConfig, deduction?.deductionTypeId]);

  useEffect(() => {
    const fetchDisputeTypes = async () => {
      try {
        setIsDisputeTypesLoading(true);
        const response = await new GetDisputeConfigUseCase(new DeductionDisputeRepository(client)).execute(
          new GetDisputeConfigDTO({
            purchaserId: deduction?.purchaserId,
          })
        );
        setIsDocumentsRequiredConfig(response.disputeConfig.isDocumentsRequiredConfig);
        setPortalKey(response.disputeConfig.portalKey);
        setDisputeTypes(response.disputeConfig.disputeTypes);
        setVendorNumber(response.disputeConfig.vendorNumber);
        setNoteTemplate(response.disputeConfig.noteTemplate);
        setDataProviderType(response.disputeConfig.dataProviderType);
        if (response.disputeConfig?.disputeContacts?.length > 0) {
          setDisputeContactInfoList(
            response.disputeConfig.disputeContacts.map(contact => ({
              label: `${contact.name} - ${contact.email}`,
              value: contact.email,
            }))
          );
        }

        if (response.disputeConfig?.distributionCenters && response.disputeConfig.distributionCenters.length > 0) {
          setDistributionCenters(response.disputeConfig.distributionCenters);
        }
      } catch (error) {
        logger().error("Error fetching dispute types", error);
        errorToast({
          content: "Failed to fetch dispute types",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsDisputeTypesLoading(false);
        setIsUnfiFormLoading(false);
      }
    };
    fetchDisputeTypes();
  }, [errorToast, deduction?.purchaserId, logger]);

  useEffect(() => {
    if (disputeTypes?.length > 0) {
      const findDisputeTypeByDeductionValidityRcaIdsUnfi = disputeTypes.find(type =>
        type.deductionValidityRcaIds?.some(id => deduction?.rcaIds.includes(id))
      );
      const findDisputeTypeByDeductionSubTypeIdUnfi = disputeTypes.find(
        type =>
          type.deductionTypeIds?.includes(deduction?.deductionSubTypeId) ||
          type.deductionTypeIds?.includes(deduction?.deductionTypeId)
      );

      if (findDisputeTypeByDeductionValidityRcaIdsUnfi) {
        disputeForm.setFieldValue("disputeType", {
          label: findDisputeTypeByDeductionValidityRcaIdsUnfi.disputeTypeName,
          value: findDisputeTypeByDeductionValidityRcaIdsUnfi.disputeTypeId?.toString(),
        });
      } else if (findDisputeTypeByDeductionSubTypeIdUnfi) {
        disputeForm.setFieldValue("disputeType", {
          label: findDisputeTypeByDeductionSubTypeIdUnfi.disputeTypeName,
          value: findDisputeTypeByDeductionSubTypeIdUnfi.disputeTypeId?.toString(),
        });
      } else if (isSingleDisputeType) {
        disputeForm.setFieldValue("disputeType", getDisputeTypes()[0]);
      }
    }

    if (documents?.length > 0) {
      const sortedDocuments = [...documents];
      disputeForm.setFieldValue(
        "documentsIds",
        sortedDocuments.filter(document => !document.deletable).map(document => document.id.toString())
      );
    }

    disputeForm.setFieldValue("totalAmount", remittanceItemNetAmount);
    disputeForm.setFieldValue("disputeAmount", deductionInvoiceAmount);
    disputeForm.setFieldsValue({
      disputeRequestMessage: noteTemplate ?? "",
    });

    if (isPortalForm) {
      return;
    }

    if (disputeContactInfoList.length === 0 && user?.email) {
      disputeForm.setFieldValue("contactName", user.fullName);
      disputeForm.setFieldValue("contactEmail", user.email);
    }

    if (disputeContactInfoList.length === 1) {
      const contactInfo = disputeContactInfoList[0];
      disputeForm.setFieldValue("contactName", contactInfo.label);
      disputeForm.setFieldValue("contactEmail", contactInfo.value);
      disputeForm.setFieldValue("contactInfo", { label: contactInfo.label, value: contactInfo.value });
    }

    disputeForm.setFieldValue("checkNumber", checkNumber);
    disputeForm.setFieldValue("invoiceNumber", claimNumber);
    disputeForm.setFieldValue("poNumber", deduction?.poNumber);
    disputeForm.setFieldValue("brandName", workspace?.workspaceName);
    disputeForm.setFieldValue("vendorNumber", vendorNumber);
    if (deduction?.distributionCenterCode && distributionCenters?.length > 0) {
      disputeForm.setFieldValue("distributionCenter", {
        label: distributionCenters?.find(dc => dc.dcCode === deduction?.distributionCenterCode)?.dcName,
        value: distributionCenters?.find(dc => dc.dcCode === deduction?.distributionCenterCode)?.dcName?.toString(),
      });
    }
  }, [
    documents,
    remittanceItemNetAmount,
    deductionInvoiceAmount,
    deduction?.purchaserId,
    disputeContactInfoList,
    user,
    disputeForm,
    disputeTypes,
    deduction?.rcaIds,
    deduction?.deductionTypeId,
    deduction?.deductionSubTypeId,
    workspace?.workspaceName,
    vendorNumber,
    checkNumber,
    claimNumber,
    deduction?.poNumber,
    deduction?.distributionCenterCode,
    distributionCenters,
    noteTemplate,
    isPortalForm,
  ]);

  const buildPortalDisputeData = (values: RaiseDisputeUnfiPortalFormValues): DisputeDataForSubmit => ({
    disputeTypeId: Number(values.disputeType.value),
    disputeAmount: Number(values.disputeAmount),
    totalAmount: Number(values.totalAmount ?? remittanceItemNetAmount),
    disputeRequestMessage: values.disputeRequestMessage.toString().trim(),
    portalKey: portalKey ?? "",
    documentIds: values.documentsIds?.map(Number) ?? [],
    dataProviderType: dataProviderType ?? undefined,
  });

  const resolveContactInfo = (values: RaiseDisputeUnfiFormValues): { name: string; email: string } => {
    if (disputeContactInfoList.length > 1 && values.contactInfo) {
      return {
        name: values.contactInfo.label.split("-")[0].trim(),
        email: values.contactInfo.value.trim(),
      };
    }

    if (disputeContactInfoList.length === 1) {
      const contact = disputeContactInfoList[0];
      return {
        name: contact.label.split("-")[0].trim(),
        email: contact.value.trim(),
      };
    }

    if (user?.email) {
      return {
        name: user.fullName,
        email: user.email,
      };
    }

    return {
      name: values.contactName,
      email: values.contactEmail,
    };
  };

  const buildUnfiEmailDisputeData = (values: RaiseDisputeUnfiFormValues): DisputeDataForSubmit => {
    const distributionCenterValue =
      values.distributionCenter?.value ??
      distributionCenters?.find(dc => dc.dcCode === deduction?.distributionCenterCode)?.dcName ??
      undefined;

    return {
      disputeTypeId: Number(values.disputeType.value),
      documentIds: values.documentsIds?.map(Number) ?? [],
      brandName: values.brandName ?? workspace?.workspaceName,
      vendorNumber: values.vendorNumber ?? vendorNumber ?? undefined,
      distributionCenter: distributionCenterValue,
      disputeAmount: Number(values.disputeAmount),
      totalAmount: Number(values.totalAmount),
      disputeRequestMessage: values.disputeRequestMessage.toString().trim(),
      portalKey: portalKey ?? "",
      poNumber: values.poNumber ?? deduction?.poNumber,
      checkNumber: values.checkNumber ?? checkNumber,
      invoiceNumber: values.invoiceNumber ?? claimNumber,
      contactInfo: resolveContactInfo(values),
      dataProviderType: dataProviderType ?? undefined,
    };
  };

  const buildUnfiEmailDisputeDataFromPortal = (values: RaiseDisputeUnfiPortalFormValues): DisputeDataForSubmit => ({
    ...buildPortalDisputeData(values),
    brandName: workspace?.workspaceName,
    vendorNumber: vendorNumber ?? undefined,
    distributionCenter:
      distributionCenters?.find(dc => dc.dcCode === deduction?.distributionCenterCode)?.dcName ?? undefined,
    poNumber: deduction?.poNumber,
    checkNumber,
    invoiceNumber: claimNumber,
    contactInfo: resolveContactInfo(values as RaiseDisputeUnfiFormValues),
  });

  const handleSubmit = (values: RaiseDisputeUnfiFormValues) => {
    if (!portalKey) {
      return;
    }

    if (isPortalForm) {
      const disputeData = buildPortalDisputeData(values);

      // SuperValu is portal-only (no email preview now). UNFI portal dispute submits immediately.
      if (isPortalOnlyPurchaser || isPortalDisputeSubmitKey(portalKey)) {
        onDisputeSubmit(disputeData);
        return;
      }

      onUnfiEmailPreview(buildUnfiEmailDisputeDataFromPortal(values));
      return;
    }

    const disputeData = buildUnfiEmailDisputeData(values);
    logger().info("Dispute data", disputeData);
    onUnfiEmailPreview(disputeData);
  };

  const getFormContent = () => {
    if (isPortalForm) {
      return (
        <UnfiPortalFormContent
          getDisputeTypes={getDisputeTypes}
          isDisputeTypesLoading={isDisputeTypesLoading}
          isSingleDisputeType={isSingleDisputeType}
        />
      );
    }

    return (
      <UnfiEmailFormContent
        disputeForm={disputeForm}
        getDisputeTypes={getDisputeTypes}
        getDistributionCenters={getDistributionCenters}
        isDisputeTypesLoading={isDisputeTypesLoading}
        disputeContactInfoList={disputeContactInfoList}
        deduction={deduction}
      />
    );
  };

  return isUnfiFormLoading ? (
    <Spin size="large" spinning={isUnfiFormLoading} />
  ) : (
    <Form form={disputeForm} onFinish={handleSubmit}>
      {getFormContent()}

      <DisputeNotesField
        form={disputeForm}
        deduction={deduction}
        checkNumber={checkNumber}
        invoiceNumber={claimNumber}
        noteTemplate={noteTemplate}
      />

      {isDocumentsRequired && (
        <Form.Item
          name="documentsIds"
          rules={[{ required: true, message: "Select at least one document." }]}
          label="Document Selection"
        >
          <CheckboxGroup
            options={getUnfiDocuments()}
            size="medium"
            variant="group"
            color="primary"
            defaultValue={documents.filter(document => !document.deletable).map(document => document.id.toString())}
          />
        </Form.Item>
      )}
    </Form>
  );
};

export default RiseDisputeUnfiForm;
